import React, { useState, useEffect } from 'react';
import { ViewState } from '../types';
import { Menu, X, ShoppingBag } from 'lucide-react';
import { useLanguage } from '../services/languageContext';

interface NavbarProps {
  currentView: ViewState;
  onChangeView: (view: ViewState) => void;
  cartCount: number;
  onCartClick?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ currentView, onChangeView, cartCount, onCartClick }) => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 flex justify-center ${isScrolled ? 'pt-2 md:pt-4' : 'pt-4 md:pt-6'}`}>
        <div className={`
          relative flex items-center justify-between px-4 sm:px-6 md:px-8 
          transition-all duration-500
          ${isScrolled 
            ? 'w-[95%] md:w-[60%] bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-full py-3 shadow-glass' 
            : 'w-full max-w-[1600px] py-4 bg-transparent border-transparent'
          }
        `}>
          
          {/* Brand */}
          <div 
            className="flex items-center gap-3 cursor-pointer group z-20"
            onClick={() => onChangeView('landing')}
          >
            <img 
              src="https://lh3.googleusercontent.com/rd-d/ALs6j_Gkvl9xgMaWh2LGR8Y5xiS0au5rMfY2o8sHQyx8MQgJ4I2U-XDXbgE65799Es575wm7EooTgLBUF_YDeZ2RO7yU9TIh8siQEOz479vrKjVwqS7ObAACHPCWfRGWXaSgGIiE4vv7UY5xQI4puchcfV0EKFkpQBRH1s5cq2g9_9DXh8BQc9zM8rdoJs73vmst-Vi2H7oFBZUMX5XNj52S2nRF0vrpT2Iz3TQUSBDs2UR7zhzPmOd6kgC1e5S7OEGqRyVGsBU8YKXxHomMmBFdSCibCVUMn1oQpM2ukr7VNKOwpWE43k2wiHB63MN_ObIchrByidbAThmpMyJbNYZp7cFcppp9jUDcIGotoyUOmk3NHSuqC-i2Mllp1liB073ZJv_-lIZMVdZX6DKMYMXsSwgegtl-_8a5hXgFB3IZfwjIRTd7jGdSB_ZDIOIOCeBwMaxMkR5z593w2tHiticQOfU1UYfu9ceg-kWwy9yG71E_dZoH5krVDgIxJdibBVCTELpCiH3SIPriVmv9_E650s0d865ZTDlqT63QUsjfS-JrC-Q45VpjcmSThqHQm3jgI8fAxCqOg0oifTq7e4izchwwNxOZThDbyrBhMqiVPpsqza4hUdN2gfvDstpDN8V0yN0dDM4Jagaz3mwfIae6miUduFdSFUE80Los64dOs1rTKbBR0qdSUm3k4Z5XDXt_Y66Skcc_3wyBtMvoVJWcBeODjAFmzOf5bGRKe_h6rhbSkUk60Q3D6y4j6fsfnJeRhz4VcEk6tDpsQiNYwaBpYO84HPnxuew5ZYDklONwg7xdvJM4UDzo7jc1RsQ3oTj-Wk2HyRl4P4Ebi3qsr562khKvlg8iKq-jFvzhOG9eDPSkUcU0IFPeikq1Hy9oQ-OKTHJp96zUmbc5ju2WGHmTw1eExPPyjaUSt5jzk3npgrH6BZlicy1PkTm1utHx41KoBllYiC3lRhw7JRHSFubpL0gP0W0ZTpmCZJsgs67iPASBq_LCbmVMu1adLHB00EztiWLtrkKnefWLmPkoTfyPK89Sf5YdIzZf9fU3E1mAH14QdfUG5OqcB4-zdps2FGuzzA_yi_BG_kX7qt_EmK6qquCwwBbvVt_iMDqtNAzcy9wflXsnvx3Mzzk416C4pQ1W4a4Ozdf8lU6ppJdQu2zRZyoCwA8cy7c1sJ9ACLAvTmyzR0ZK4_DKgk_0Qfudf_DyZqbMywAavk88rLLKVoGV_yirxlWkn-lEaHvbx5xXvQRP=w2880-h1640?auditContext=prefetch" 
              alt="OFM HUB" 
              className="w-10 h-10 rounded-full object-cover shadow-[0_0_15px_rgba(255,255,255,0.15)] group-hover:shadow-[0_0_25px_rgba(255,255,255,0.3)] transition-all bg-indigo-500/10 border border-white/10"
            />
            <span className="font-display font-bold text-sm sm:text-lg tracking-wide group-hover:text-gray-300 transition-colors">OFM HUB</span>
          </div>

          {/* Desktop Nav - ABSOLUTE CENTER */}
          <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-2 bg-white/[0.03] px-1.5 py-1.5 rounded-full border border-white/[0.05] backdrop-blur-md z-10">
             {['landing', 'shop'].map((view) => (
               <button 
                 key={view}
                 onClick={() => onChangeView(view as ViewState)}
                 className={`
                   px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-300
                   ${currentView === view 
                     ? 'bg-white text-black shadow-lg' 
                     : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
                   }
                 `}
               >
                 {view === 'landing' ? t('nav_home') : t('nav_market')}
               </button>
             ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-4 z-20">
            <button 
              className="relative p-2.5 bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.05] rounded-full transition-all group"
              onClick={onCartClick}
            >
              <ShoppingBag size={18} className="text-gray-300 group-hover:text-white transition-colors" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-black text-[9px] font-bold flex items-center justify-center rounded-full shadow-glow">
                  {cartCount}
                </span>
              )}
            </button>
            
            <div className="md:hidden">
              <button onClick={() => setIsMobileMenuOpen(true)} className="text-white p-2 hover:bg-white/10 rounded-full transition-colors">
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-[#030304]/98 backdrop-blur-3xl z-[60] flex flex-col p-8 animate-fade-in overscroll-none">
          <div className="flex justify-between items-center mb-16">
            <div className="flex items-center gap-3">
                <img src="https://lh3.googleusercontent.com/rd-d/ALs6j_Gkvl9xgMaWh2LGR8Y5xiS0au5rMfY2o8sHQyx8MQgJ4I2U-XDXbgE65799Es575wm7EooTgLBUF_YDeZ2RO7yU9TIh8siQEOz479vrKjVwqS7ObAACHPCWfRGWXaSgGIiE4vv7UY5xQI4puchcfV0EKFkpQBRH1s5cq2g9_9DXh8BQc9zM8rdoJs73vmst-Vi2H7oFBZUMX5XNj52S2nRF0vrpT2Iz3TQUSBDs2UR7zhzPmOd6kgC1e5S7OEGqRyVGsBU8YKXxHomMmBFdSCibCVUMn1oQpM2ukr7VNKOwpWE43k2wiHB63MN_ObIchrByidbAThmpMyJbNYZp7cFcppp9jUDcIGotoyUOmk3NHSuqC-i2Mllp1liB073ZJv_-lIZMVdZX6DKMYMXsSwgegtl-_8a5hXgFB3IZfwjIRTd7jGdSB_ZDIOIOCeBwMaxMkR5z593w2tHiticQOfU1UYfu9ceg-kWwy9yG71E_dZoH5krVDgIxJdibBVCTELpCiH3SIPriVmv9_E650s0d865ZTDlqT63QUsjfS-JrC-Q45VpjcmSThqHQm3jgI8fAxCqOg0oifTq7e4izchwwNxOZThDbyrBhMqiVPpsqza4hUdN2gfvDstpDN8V0yN0dDM4Jagaz3mwfIae6miUduFdSFUE80Los64dOs1rTKbBR0qdSUm3k4Z5XDXt_Y66Skcc_3wyBtMvoVJWcBeODjAFmzOf5bGRKe_h6rhbSkUk60Q3D6y4j6fsfnJeRhz4VcEk6tDpsQiNYwaBpYO84HPnxuew5ZYDklONwg7xdvJM4UDzo7jc1RsQ3oTj-Wk2HyRl4P4Ebi3qsr562khKvlg8iKq-jFvzhOG9eDPSkUcU0IFPeikq1Hy9oQ-OKTHJp96zUmbc5ju2WGHmTw1eExPPyjaUSt5jzk3npgrH6BZlicy1PkTm1utHx41KoBllYiC3lRhw7JRHSFubpL0gP0W0ZTpmCZJsgs67iPASBq_LCbmVMu1adLHB00EztiWLtrkKnefWLmPkoTfyPK89Sf5YdIzZf9fU3E1mAH14QdfUG5OqcB4-zdps2FGuzzA_yi_BG_kX7qt_EmK6qquCwwBbvVt_iMDqtNAzcy9wflXsnvx3Mzzk416C4pQ1W4a4Ozdf8lU6ppJdQu2zRZyoCwA8cy7c1sJ9ACLAvTmyzR0ZK4_DKgk_0Qfudf_DyZqbMywAavk88rLLKVoGV_yirxlWkn-lEaHvbx5xXvQRP=w2880-h1640?auditContext=prefetch" alt="OFM HUB" className="w-10 h-10 rounded-full" />
                <span className="font-display font-bold text-xl">OFM HUB</span>
            </div>
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 border border-white/10 rounded-full bg-white/5"><X size={24} /></button>
          </div>
          
          <div className="flex flex-col gap-6">
            <button onClick={() => { onChangeView('landing'); setIsMobileMenuOpen(false); }} className="text-left font-display text-4xl font-bold hover:text-indigo-400 transition-colors flex items-center justify-between group border-b border-white/5 pb-4">
                <span>{t('nav_home')}</span>
                <span className="text-sm text-gray-600 group-hover:text-white transition-colors">01</span>
            </button>
            <button onClick={() => { onChangeView('shop'); setIsMobileMenuOpen(false); }} className="text-left font-display text-4xl font-bold hover:text-indigo-400 transition-colors flex items-center justify-between group border-b border-white/5 pb-4">
                <span>{t('nav_market')}</span>
                <span className="text-sm text-gray-600 group-hover:text-white transition-colors">02</span>
            </button>
          </div>
          
          <div className="mt-auto">
             <div className="p-6 bg-white/5 rounded-2xl border border-white/5">
                <p className="text-gray-400 text-xs mb-2">Need Help?</p>
                <a href="https://t.me/miranovui" target="_blank" className="text-white font-bold text-sm hover:underline">Contact Support</a>
             </div>
             <div className="mt-8 text-center text-gray-600 text-[10px] uppercase tracking-widest">
                Harvard Design '25
             </div>
          </div>
        </div>
      )}
    </>
  );
};